/* FinancialAnalyticsDashboard.js — Business Financial Analytics
   Tabs: Overview, Revenue, Expenses, P&L, Invoices, Payroll, Budgets, Tax
   Features: KPI cards, trend charts, currency formatting, period comparison,
   company logo, export, forecast, alerts */

import React, { useState, useCallback, useMemo } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView, RefreshControl,
  StatusBar, Modal, ActivityIndicator, Dimensions, Share, Image, TextInput, FlatList,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import { useAI, AIChatWidget } from '../../../services/AIBrainService';

const { width } = Dimensions.get('window');
const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const api = axios.create({ baseURL: API_BASE_URL, timeout: 15000, headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY } });
api.interceptors.request.use(async c => { try { const t = await AsyncStorage.getItem('auth_token'); if (t) c.headers.Authorization = 'Bearer ' + t; } catch {} return c; });
const safeData = r => (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data : [];

// ── Currency & Formatting ────────────────────────────────────────────────
const fmtGBP = (v, dec = 0) => '£' + (parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: dec, maximumFractionDigits: dec });
const fmtK = v => { const n = parseFloat(v) || 0; return n >= 1000000 ? '£' + (n / 1000000).toFixed(1) + 'M' : n >= 1000 ? '£' + (n / 1000).toFixed(1) + 'K' : '£' + n.toFixed(0); };
const fmtPct = (v, d = 1) => (parseFloat(v) || 0).toFixed(d) + '%';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
const fmtMonth = d => d ? new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : '-';

// ── Colors ────────────────────────────────────────────────────────────────
const C = {
  primary: '#0F172A', accent: '#3B82F6', success: '#059669', danger: '#DC2626',
  warning: '#D97706', purple: '#7C3AED', pink: '#EC4899', teal: '#0D9488',
  orange: '#EA580C', slate: '#64748B', bg: '#F8FAFC',
  revenue: '#059669', expense: '#DC2626', profit: '#3B82F6', payroll: '#7C3AED',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════════════
export default function FinancialAnalyticsDashboard({ navigation, route }) {
  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || route?.params?.companyProfile?.logo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || route?.params?.companyProfile?.name || '');
  const [companyCurrency, setCompanyCurrency] = useState('GBP');

  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const { ask: askAI, agents: aiAgents, loading: aiLoading, history: aiHistory } = useAI('FinancialAnalyticsDashboard');
  const [showAIChat, setShowAIChat] = useState(false);

  const [selectedPeriod, setSelectedPeriod] = useState('month'); // month, quarter, year, ytd
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailData, setDetailData] = useState(null);

  // ── Financial Data ──
  const [invoices, setInvoices] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [payroll, setPayroll] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [employees, setEmployees] = useState([]);

  // ── Computed KPIs ──
  const [kpis, setKpis] = useState({
    totalRevenue: 0, totalExpenses: 0, netProfit: 0, profitMargin: 0,
    outstanding: 0, overdue: 0, cashBalance: 0, payrollCost: 0,
    invoiceCount: 0, paidCount: 0, pendingCount: 0, overdueCount: 0,
    avgInvoice: 0, revenueGrowth: 0, expenseGrowth: 0, employeeCount: 0,
    costPerEmployee: 0, revenuePerEmployee: 0, taxLiability: 0, vatDue: 0,
    budgetUsed: 0, budgetTotal: 0, budgetRemaining: 0,
  });

  // ── Monthly Breakdown ──
  const [monthlyData, setMonthlyData] = useState([]);
  const [healthScore, setHealthScore] = useState(0);
  const [cashFlowData, setCashFlowData] = useState({ inflows: 0, outflows: 0, net: 0, runway: 0 });
  const [forecastData, setForecastData] = useState({ revForecast: [], expForecast: [], profitForecast: [] });
  const [agingBuckets, setAgingBuckets] = useState({ current: 0, d30: 0, d60: 0, d90: 0, d90plus: 0 });
  const [showExportModal, setShowExportModal] = useState(false);

  // ── Load Data ──
  useFocusEffect(useCallback(() => { loadAll(); }, []));

  const loadAll = async () => {
    setIsLoading(true);
    try {
      const cid = await AsyncStorage.getItem('company_id') || '1';

      // Load company profile
      try {
        const cached = await AsyncStorage.getItem('company_profile');
        if (cached) { const p = JSON.parse(cached); if (p.logo) setCompanyLogo(p.logo); if (p.name || p.company_name) setCompanyName(p.name || p.company_name); if (p.currency) setCompanyCurrency(p.currency); }
        const pr = await api.get(API_BASE_URL + '/api/data/company_profiles', { params: { company_id: parseInt(cid) } });
        const pd = safeData(pr);
        if (pd.length > 0) { const p = pd[0]; if (p.logo) setCompanyLogo(p.logo); if (p.name || p.company_name) setCompanyName(p.name || p.company_name); if (p.currency) setCompanyCurrency(p.currency); }
      } catch {}

      // Load financial data
      const p = { company_id: parseInt(cid), limit: 500 };
      const [r1, r2, r3, r4, r5, r6] = await Promise.allSettled([
        api.get(API_BASE_URL + '/api/data/invoices', { params: p }).catch(() => null),
        api.get(API_BASE_URL + '/api/data/expenses', { params: p }).catch(() => null),
        api.get(API_BASE_URL + '/api/data/payments', { params: p }).catch(() => null),
        api.get(API_BASE_URL + '/api/data/payroll_records', { params: p }).catch(() => null),
        api.get(API_BASE_URL + '/api/data/budgets', { params: p }).catch(() => null),
        api.get(API_BASE_URL + '/api/data/employees', { params: p }).catch(() => null),
      ]);

      const inv = safeData(r1.status === 'fulfilled' ? r1.value : null);
      const exp = safeData(r2.status === 'fulfilled' ? r2.value : null);
      const pay = safeData(r3.status === 'fulfilled' ? r3.value : null);
      const pr = safeData(r4.status === 'fulfilled' ? r4.value : null);
      const bud = safeData(r5.status === 'fulfilled' ? r5.value : null);
      const emp = safeData(r6.status === 'fulfilled' ? r6.value : null);

      setInvoices(inv); setExpenses(exp); setPayments(pay);
      setPayroll(pr); setBudgets(bud); setEmployees(emp);

      // ── Calculate KPIs ──
      const totalRev = inv.reduce((s, i) => s + (parseFloat(i.total || i.amount || i.total_amount || 0)), 0);
      const totalExp = exp.reduce((s, e) => s + (parseFloat(e.amount || e.total || 0)), 0);
      const paidInv = inv.filter(i => (i.status || '').toLowerCase() === 'paid');
      const pendingInv = inv.filter(i => (i.status || '').toLowerCase() === 'pending' || (i.status || '').toLowerCase() === 'sent');
      const overdueInv = inv.filter(i => (i.status || '').toLowerCase() === 'overdue');
      const outstanding = pendingInv.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0) + overdueInv.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);
      const overdue = overdueInv.reduce((s, i) => s + parseFloat(i.total || i.amount || 0), 0);
      const payrollCost = pr.reduce((s, p) => s + (parseFloat(p.gross_pay || p.total || p.amount || 0)), 0);
      const netProfit = totalRev - totalExp - payrollCost;
      const profitMargin = totalRev > 0 ? (netProfit / totalRev) * 100 : 0;
      const avgInv = inv.length > 0 ? totalRev / inv.length : 0;
      const empCount = emp.filter(e => (e.status || '').toLowerCase() === 'active').length || emp.length;
      const budgetTotal = bud.reduce((s, b) => s + parseFloat(b.total || b.amount || b.budget_amount || 0), 0);
      const budgetUsed = bud.reduce((s, b) => s + parseFloat(b.spent || b.used || b.actual || 0), 0);

      // Tax estimates (UK: 19% corporation tax, 20% VAT standard)
      const taxLiability = netProfit > 0 ? netProfit * 0.19 : 0;
      const vatDue = totalRev * 0.2 - totalExp * 0.2; // Simplified VAT calc

      setKpis({
        totalRevenue: totalRev, totalExpenses: totalExp + payrollCost, netProfit, profitMargin,
        outstanding, overdue, cashBalance: totalRev - totalExp - payrollCost - overdue,
        payrollCost, invoiceCount: inv.length, paidCount: paidInv.length,
        pendingCount: pendingInv.length, overdueCount: overdueInv.length,
        avgInvoice: avgInv, revenueGrowth: 0, expenseGrowth: 0,
        employeeCount: empCount,
        costPerEmployee: empCount > 0 ? (totalExp + payrollCost) / empCount : 0,
        revenuePerEmployee: empCount > 0 ? totalRev / empCount : 0,
        taxLiability, vatDue: Math.max(0, vatDue),
        budgetUsed, budgetTotal, budgetRemaining: budgetTotal - budgetUsed,
      });

      // ── Monthly Breakdown ──
      const monthly = {};
      MONTHS.forEach((m, i) => { monthly[i] = { month: m, revenue: 0, expenses: 0, profit: 0, invoices: 0, payroll: 0 }; });
      inv.forEach(i => { const m = new Date(i.created_at || i.invoice_date || i.date || new Date()).getMonth(); if (monthly[m]) { monthly[m].revenue += parseFloat(i.total || i.amount || 0); monthly[m].invoices++; } });
      exp.forEach(e => { const m = new Date(e.created_at || e.expense_date || e.date || new Date()).getMonth(); if (monthly[m]) monthly[m].expenses += parseFloat(e.amount || e.total || 0); });
      pr.forEach(p => { const m = new Date(p.created_at || p.pay_date || p.date || new Date()).getMonth(); if (monthly[m]) monthly[m].payroll += parseFloat(p.gross_pay || p.total || p.amount || 0); });
      Object.values(monthly).forEach(m => { m.profit = m.revenue - m.expenses - m.payroll; });
      setMonthlyData(Object.values(monthly));

      // ── Health Score ──
      const scores = [];
      if (profitMargin > 15) scores.push(25); else if (profitMargin > 5) scores.push(15); else if (profitMargin > 0) scores.push(8); else scores.push(0);
      if (kpis.overdueCount === 0) scores.push(25); else if (overdueInv.length < inv.length * 0.1) scores.push(15); else scores.push(5);
      const budPct = budgetTotal > 0 ? (budgetUsed / budgetTotal) : 0;
      if (budPct < 0.7) scores.push(25); else if (budPct < 0.9) scores.push(15); else scores.push(5);
      if (empCount > 0 && totalRev / empCount > 50000) scores.push(25); else if (empCount > 0 && totalRev / empCount > 25000) scores.push(15); else scores.push(10);
      setHealthScore(scores.reduce((a, b) => a + b, 0));

      // ── Cash Flow ──
      const inflows = totalRev;
      const outflows = totalExp + payrollCost + kpis.taxLiability;
      const netCF = inflows - outflows;
      const monthlyBurn = (totalExp + payrollCost) / 12;
      const runway = monthlyBurn > 0 ? Math.max(0, netCF / monthlyBurn) : 99;
      setCashFlowData({ inflows, outflows, net: netCF, runway, monthlyBurn });

      // ── Forecast (simple linear from monthly data) ──
      const activeMonths = Object.values(monthly).filter(m => m.revenue > 0 || m.expenses > 0);
      const avgRevMonth = activeMonths.length > 0 ? activeMonths.reduce((s, m) => s + m.revenue, 0) / activeMonths.length : 0;
      const avgExpMonth = activeMonths.length > 0 ? activeMonths.reduce((s, m) => s + m.expenses + m.payroll, 0) / activeMonths.length : 0;
      const revTrend = activeMonths.length >= 2 ? (activeMonths[activeMonths.length-1]?.revenue - activeMonths[0]?.revenue) / (activeMonths.length * (activeMonths[0]?.revenue || 1)) : 0;
      const nextMonths = ['Next 1mo', 'Next 2mo', 'Next 3mo', 'Next 6mo', 'Next 12mo'];
      const multipliers = [1, 2, 3, 6, 12];
      setForecastData({
        revForecast: multipliers.map((m, i) => ({ label: nextMonths[i], value: avgRevMonth * m * (1 + revTrend * m) })),
        expForecast: multipliers.map((m, i) => ({ label: nextMonths[i], value: avgExpMonth * m })),
        profitForecast: multipliers.map((m, i) => ({ label: nextMonths[i], value: (avgRevMonth * (1 + revTrend * m) - avgExpMonth) * m })),
      });

      // ── Aged Receivables ──
      const now = new Date();
      const aging = { current: 0, d30: 0, d60: 0, d90: 0, d90plus: 0 };
      [...pendingInv, ...overdueInv].forEach(i => {
        const due = new Date(i.due_date || i.created_at || now);
        const days = Math.floor((now - due) / 86400000);
        const amt = parseFloat(i.total || i.amount || 0);
        if (days <= 0) aging.current += amt;
        else if (days <= 30) aging.d30 += amt;
        else if (days <= 60) aging.d60 += amt;
        else if (days <= 90) aging.d90 += amt;
        else aging.d90plus += amt;
      });
      setAgingBuckets(aging);

      // Cache
      await AsyncStorage.setItem('financial_cache', JSON.stringify({ inv, exp, pay, pr, bud, emp, kpis, monthlyData: Object.values(monthly) }));
    } catch (e) {
      console.warn('Financial load error:', e.message);
      try { const c = await AsyncStorage.getItem('financial_cache'); if (c) { const d = JSON.parse(c); setInvoices(d.inv || []); setExpenses(d.exp || []); setPayments(d.pay || []); setPayroll(d.pr || []); setBudgets(d.bud || []); setEmployees(d.emp || []); if (d.kpis) setKpis(d.kpis); if (d.monthlyData) setMonthlyData(d.monthlyData); } } catch {}
    } finally { setIsLoading(false); }
  };
  const onRefresh = async () => { setRefreshing(true); await loadAll(); setRefreshing(false); };

  // ── Export ──
  const exportReport = () => {
    const lines = [
      `FINANCIAL REPORT — ${companyName}`,
      `Generated: ${new Date().toLocaleDateString('en-GB')}`,
      `Period: ${selectedPeriod.toUpperCase()}`,
      '',
      `SUMMARY`,
      `Revenue: ${fmtGBP(kpis.totalRevenue)}`,
      `Expenses: ${fmtGBP(kpis.totalExpenses)}`,
      `Net Profit: ${fmtGBP(kpis.netProfit)}`,
      `Profit Margin: ${fmtPct(kpis.profitMargin)}`,
      `Outstanding: ${fmtGBP(kpis.outstanding)}`,
      `Overdue: ${fmtGBP(kpis.overdue)}`,
      `Payroll: ${fmtGBP(kpis.payrollCost)}`,
      `Employees: ${kpis.employeeCount}`,
      `Corp Tax Est: ${fmtGBP(kpis.taxLiability)}`,
      `VAT Due Est: ${fmtGBP(kpis.vatDue)}`,
      '',
      'MONTHLY BREAKDOWN',
      ...monthlyData.filter(m => m.revenue > 0 || m.expenses > 0).map(m =>
        `${m.month}: Rev ${fmtGBP(m.revenue)} | Exp ${fmtGBP(m.expenses)} | P&L ${fmtGBP(m.profit)}`
      ),
    ];
    Share.share({ message: lines.join('\n'), title: 'Financial Report' });
  };

  // ── Mini Bar Chart Component ──
  const MiniBar = ({ data, color, maxVal, label }) => {
    const max = maxVal || Math.max(...data.map(d => d.value), 1);
    return (<View style={{ marginBottom: 16 }}>
      {label && <Text style={{ fontSize: 12, fontWeight: '700', color: '#334155', marginBottom: 6 }}>{label}</Text>}
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', height: 60, gap: 2 }}>
        {data.map((d, i) => (<View key={i} style={{ flex: 1, alignItems: 'center' }}>
          <View style={{ width: '80%', height: Math.max(2, (d.value / max) * 52), backgroundColor: color || C.accent, borderRadius: 2 }} />
          <Text style={{ fontSize: 7, color: '#94A3B8', marginTop: 2 }}>{d.label}</Text>
        </View>))}
      </View>
    </View>);
  };

  // ── KPI Card ──
  const KPICard = ({ title, value, subtitle, icon, color, trend, onPress, size = 'half' }) => (
    <TouchableOpacity style={[st.kpiCard, size === 'full' && { width: width - 32 }]} onPress={onPress} activeOpacity={0.85}>
      <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
        <View style={[st.kpiIcon, { backgroundColor: (color || C.accent) + '15' }]}>
          <MaterialCommunityIcons name={icon || 'currency-gbp'} size={18} color={color || C.accent} /></View>
        <Text style={st.kpiTitle}>{title}</Text>
        {trend != null && <View style={[st.trendBadge, { backgroundColor: trend >= 0 ? C.success + '15' : C.danger + '15' }]}>
          <MaterialCommunityIcons name={trend >= 0 ? 'trending-up' : 'trending-down'} size={12} color={trend >= 0 ? C.success : C.danger} />
          <Text style={{ fontSize: 10, fontWeight: '700', color: trend >= 0 ? C.success : C.danger, marginLeft: 2 }}>{fmtPct(Math.abs(trend))}</Text></View>}
      </View>
      <Text style={[st.kpiValue, { color: color || C.primary }]}>{value}</Text>
      {subtitle && <Text style={st.kpiSub}>{subtitle}</Text>}
    </TouchableOpacity>);

  // ── Progress Bar ──
  const ProgressBar = ({ value, max, color, label, showAmount }) => {
    const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
    return (<View style={{ marginBottom: 10 }}>
      {label && <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 4 }}>
        <Text style={{ fontSize: 12, color: '#475569', fontWeight: '500' }}>{label}</Text>
        {showAmount && <Text style={{ fontSize: 12, fontWeight: '700', color: color || C.accent }}>{fmtGBP(value)} / {fmtGBP(max)}</Text>}
      </View>}
      <View style={{ height: 8, backgroundColor: '#E2E8F0', borderRadius: 4, overflow: 'hidden' }}>
        <View style={{ width: pct + '%', height: '100%', backgroundColor: color || C.accent, borderRadius: 4 }} /></View>
      <Text style={{ fontSize: 10, color: '#94A3B8', marginTop: 2, textAlign: 'right' }}>{fmtPct(pct, 0)}</Text>
    </View>);
  };

  // ══════════════════════════════════════════════════════════════════
  // TABS
  // ══════════════════════════════════════════════════════════════════
  const TABS = [
    { k: 'overview', l: 'Overview', i: 'view-dashboard' },
    { k: 'revenue', l: 'Revenue', i: 'trending-up' },
    { k: 'expenses', l: 'Expenses', i: 'trending-down' },
    { k: 'pl', l: 'P&L', i: 'scale-balance' },
    { k: 'cashflow', l: 'Cash Flow', i: 'swap-horizontal' },
    { k: 'invoices', l: 'Invoices', i: 'file-document' },
    { k: 'payroll', l: 'Payroll', i: 'account-cash' },
    { k: 'budgets', l: 'Budgets', i: 'chart-donut' },
    { k: 'tax', l: 'Tax', i: 'calculator' },
    { k: 'forecast', l: 'Forecast', i: 'crystal-ball' },
  ];

  // ══════════════════════════════════════════════════════════════════
  // TAB: OVERVIEW
  // ══════════════════════════════════════════════════════════════════
  const renderOverview = () => (<View>
    {/* Hero KPIs */}
    <View style={st.kpiRow}>
      <KPICard title="Revenue" value={fmtK(kpis.totalRevenue)} icon="arrow-up-bold-circle" color={C.success} subtitle={`${kpis.invoiceCount} invoices`} onPress={() => setActiveTab('revenue')} />
      <KPICard title="Expenses" value={fmtK(kpis.totalExpenses)} icon="arrow-down-bold-circle" color={C.danger} subtitle="Inc. payroll" onPress={() => setActiveTab('expenses')} />
      <KPICard title="Net Profit" value={fmtK(kpis.netProfit)} icon="chart-line" color={kpis.netProfit >= 0 ? C.profit : C.danger} subtitle={`Margin: ${fmtPct(kpis.profitMargin)}`} onPress={() => setActiveTab('pl')} />
      <KPICard title="Cash Balance" value={fmtK(kpis.cashBalance)} icon="bank" color={C.teal} subtitle="Estimated" />
    </View>

    {/* Alerts */}
    {kpis.overdueCount > 0 && <View style={st.alertCard}>
      <MaterialCommunityIcons name="alert-circle" size={20} color={C.danger} />
      <View style={{ flex: 1, marginLeft: 10 }}><Text style={{ fontSize: 13, fontWeight: '700', color: '#7F1D1D' }}>{kpis.overdueCount} Overdue Invoice{kpis.overdueCount > 1 ? 's' : ''}</Text>
        <Text style={{ fontSize: 12, color: '#991B1B' }}>Total overdue: {fmtGBP(kpis.overdue)}</Text></View>
      <TouchableOpacity onPress={() => setActiveTab('invoices')}><Text style={{ fontSize: 12, color: C.danger, fontWeight: '700' }}>View</Text></TouchableOpacity>
    </View>}

    {/* Financial Health Score */}
    <View style={st.card}>
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text style={st.cardTitle}>Financial Health Score</Text>
        <View style={{ backgroundColor: healthScore >= 80 ? C.success + '15' : healthScore >= 50 ? C.warning + '15' : C.danger + '15', paddingHorizontal: 10, paddingVertical: 4, borderRadius: 10 }}>
          <Text style={{ fontSize: 13, fontWeight: '900', color: healthScore >= 80 ? C.success : healthScore >= 50 ? C.warning : C.danger }}>{healthScore}/100</Text>
        </View>
      </View>
      <View style={{ height: 12, backgroundColor: '#E2E8F0', borderRadius: 6, marginTop: 12, overflow: 'hidden' }}>
        <View style={{ width: healthScore + '%', height: '100%', borderRadius: 6, backgroundColor: healthScore >= 80 ? C.success : healthScore >= 50 ? C.warning : C.danger }} />
      </View>
      <View style={{ flexDirection: 'row', gap: 6, marginTop: 10 }}>
        {[{ l: 'Profitability', s: healthScore >= 80 ? 'Strong' : healthScore >= 50 ? 'Fair' : 'Weak', c: healthScore >= 80 ? C.success : healthScore >= 50 ? C.warning : C.danger },
          { l: 'Cash Position', s: cashFlowData.net >= 0 ? 'Positive' : 'Negative', c: cashFlowData.net >= 0 ? C.success : C.danger },
          { l: 'Debt Risk', s: kpis.overdueCount === 0 ? 'Low' : kpis.overdueCount < 3 ? 'Medium' : 'High', c: kpis.overdueCount === 0 ? C.success : kpis.overdueCount < 3 ? C.warning : C.danger },
        ].map((h, i) => (
          <View key={i} style={{ flex: 1, backgroundColor: h.c + '08', borderRadius: 8, padding: 8, borderLeftWidth: 3, borderLeftColor: h.c }}>
            <Text style={{ fontSize: 9, color: '#64748B' }}>{h.l}</Text>
            <Text style={{ fontSize: 11, fontWeight: '800', color: h.c }}>{h.s}</Text>
          </View>
        ))}
      </View>
    </View>

    {/* Cash Flow Summary */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Cash Flow Summary</Text>
      <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
        <View style={{ flex: 1, backgroundColor: C.success + '08', borderRadius: 10, padding: 10, alignItems: 'center' }}>
          <MaterialCommunityIcons name="arrow-down-bold-circle" size={20} color={C.success} />
          <Text style={{ fontSize: 16, fontWeight: '900', color: C.success, marginTop: 4 }}>{fmtK(cashFlowData.inflows)}</Text>
          <Text style={{ fontSize: 9, color: '#64748B' }}>Inflows</Text>
        </View>
        <View style={{ flex: 1, backgroundColor: C.danger + '08', borderRadius: 10, padding: 10, alignItems: 'center' }}>
          <MaterialCommunityIcons name="arrow-up-bold-circle" size={20} color={C.danger} />
          <Text style={{ fontSize: 16, fontWeight: '900', color: C.danger, marginTop: 4 }}>{fmtK(cashFlowData.outflows)}</Text>
          <Text style={{ fontSize: 9, color: '#64748B' }}>Outflows</Text>
        </View>
        <View style={{ flex: 1, backgroundColor: (cashFlowData.net >= 0 ? C.profit : C.danger) + '08', borderRadius: 10, padding: 10, alignItems: 'center' }}>
          <MaterialCommunityIcons name="bank-transfer" size={20} color={cashFlowData.net >= 0 ? C.profit : C.danger} />
          <Text style={{ fontSize: 16, fontWeight: '900', color: cashFlowData.net >= 0 ? C.profit : C.danger, marginTop: 4 }}>{fmtK(cashFlowData.net)}</Text>
          <Text style={{ fontSize: 9, color: '#64748B' }}>Net Cash</Text>
        </View>
      </View>
      <TouchableOpacity style={{ marginTop: 8, alignItems: 'center' }} onPress={() => setActiveTab('cashflow')}>
        <Text style={{ fontSize: 12, color: C.accent, fontWeight: '700' }}>View Full Cash Flow Analysis →</Text>
      </TouchableOpacity>
    </View>

    {/* Efficiency Metrics */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Efficiency Metrics</Text>
      <View style={{ marginTop: 10 }}>
        {[{ l: 'Revenue per Employee', v: fmtGBP(kpis.revenuePerEmployee), c: C.teal },
          { l: 'Cost per Employee', v: fmtGBP(kpis.costPerEmployee), c: C.orange },
          { l: 'Invoice Collection Rate', v: fmtPct(kpis.invoiceCount > 0 ? (kpis.paidCount / kpis.invoiceCount * 100) : 0), c: C.success },
          { l: 'Cash Runway', v: cashFlowData.runway > 12 ? '12+ months' : cashFlowData.runway.toFixed(1) + ' months', c: cashFlowData.runway > 6 ? C.success : cashFlowData.runway > 3 ? C.warning : C.danger },
          { l: 'Operating Ratio', v: fmtPct(kpis.totalRevenue > 0 ? (kpis.totalExpenses / kpis.totalRevenue * 100) : 0), c: C.purple },
          { l: 'Overdue Rate', v: fmtPct(kpis.invoiceCount > 0 ? (kpis.overdueCount / kpis.invoiceCount * 100) : 0), c: kpis.overdueCount === 0 ? C.success : C.danger },
        ].map((m, i) => (<View key={i} style={st.metricRow}><Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{m.v}</Text></View>))}
      </View>
    </View>

    {/* Revenue Chart */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Monthly Revenue vs Expenses</Text>
      <View style={{ flexDirection: 'row', height: 100, alignItems: 'flex-end', marginTop: 12, gap: 4, paddingHorizontal: 4 }}>
        {monthlyData.map((m, i) => {
          const maxR = Math.max(...monthlyData.map(d => d.revenue), 1);
          const maxE = Math.max(...monthlyData.map(d => d.expenses + d.payroll), 1);
          const maxV = Math.max(maxR, maxE);
          return (<View key={i} style={{ flex: 1, alignItems: 'center' }}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', height: 80, gap: 1 }}>
              <View style={{ width: 6, height: Math.max(1, (m.revenue / maxV) * 76), backgroundColor: C.success, borderRadius: 2 }} />
              <View style={{ width: 6, height: Math.max(1, ((m.expenses + m.payroll) / maxV) * 76), backgroundColor: C.danger + '80', borderRadius: 2 }} />
            </View>
            <Text style={{ fontSize: 7, color: '#94A3B8', marginTop: 2 }}>{m.month}</Text>
          </View>);
        })}
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 16, marginTop: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}><View style={{ width: 10, height: 10, backgroundColor: C.success, borderRadius: 2 }} /><Text style={{ fontSize: 10, color: '#64748B' }}>Revenue</Text></View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}><View style={{ width: 10, height: 10, backgroundColor: C.danger + '80', borderRadius: 2 }} /><Text style={{ fontSize: 10, color: '#64748B' }}>Expenses</Text></View>
      </View>
    </View>

    {/* Quick Stats */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Key Metrics</Text>
      <View style={{ marginTop: 12 }}>
        {[{ l: 'Avg Invoice Value', v: fmtGBP(kpis.avgInvoice), c: C.accent },
          { l: 'Outstanding', v: fmtGBP(kpis.outstanding), c: C.warning },
          { l: 'Payroll Cost', v: fmtGBP(kpis.payrollCost), c: C.purple },
          { l: 'Revenue / Employee', v: fmtGBP(kpis.revenuePerEmployee), c: C.teal },
          { l: 'Cost / Employee', v: fmtGBP(kpis.costPerEmployee), c: C.orange },
          { l: 'Employees', v: String(kpis.employeeCount), c: C.accent },
        ].map((m, i) => (<View key={i} style={st.metricRow}>
          <Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{m.v}</Text></View>))}
      </View>
    </View>

    {/* Invoice Status */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Invoice Status</Text>
      <View style={{ flexDirection: 'row', gap: 8, marginTop: 12 }}>
        {[{ l: 'Paid', v: kpis.paidCount, c: C.success }, { l: 'Pending', v: kpis.pendingCount, c: C.warning }, { l: 'Overdue', v: kpis.overdueCount, c: C.danger }].map((s, i) => (
          <View key={i} style={{ flex: 1, backgroundColor: s.c + '10', borderRadius: 10, padding: 12, alignItems: 'center' }}>
            <Text style={{ fontSize: 22, fontWeight: '900', color: s.c }}>{s.v}</Text>
            <Text style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{s.l}</Text></View>))}
      </View>
    </View>

    {/* Budget Progress */}
    {kpis.budgetTotal > 0 && <View style={st.card}>
      <Text style={st.cardTitle}>Budget Utilisation</Text>
      <ProgressBar value={kpis.budgetUsed} max={kpis.budgetTotal} color={kpis.budgetUsed / kpis.budgetTotal > 0.9 ? C.danger : C.accent} label="Overall Budget" showAmount />
    </View>}
  </View>);

  // ══════════════════════════════════════════════════════════════════
  // TAB: REVENUE
  // ══════════════════════════════════════════════════════════════════
  const renderRevenue = () => (<View>
    <View style={st.kpiRow}>
      <KPICard title="Total Revenue" value={fmtGBP(kpis.totalRevenue)} icon="cash-multiple" color={C.success} size="full" />
    </View>
    <View style={st.card}>
      <Text style={st.cardTitle}>Revenue by Month</Text>
      <MiniBar data={monthlyData.map(m => ({ value: m.revenue, label: m.month }))} color={C.success} />
    </View>
    <View style={st.card}>
      <Text style={st.cardTitle}>Top Invoices</Text>
      {[...invoices].sort((a, b) => parseFloat(b.total || b.amount || 0) - parseFloat(a.total || a.amount || 0)).slice(0, 10).map((inv, i) => (
        <View key={inv.id || i} style={st.listRow}>
          <View style={{ flex: 1 }}>
            <Text style={st.listName}>{inv.client_name || inv.customer_name || inv.invoice_number || '#' + (inv.id || i)}</Text>
            <Text style={st.listMeta}>{fmtDate(inv.created_at || inv.invoice_date)} · {inv.status || 'pending'}</Text></View>
          <Text style={[st.listAmount, { color: C.success }]}>{fmtGBP(inv.total || inv.amount)}</Text>
        </View>))}
    </View>
  </View>);

  // ══════════════════════════════════════════════════════════════════
  // TAB: EXPENSES
  // ══════════════════════════════════════════════════════════════════
  const renderExpenses = () => {
    // Group by category
    const categories = {};
    expenses.forEach(e => { const cat = e.category || e.expense_type || 'Other'; categories[cat] = (categories[cat] || 0) + parseFloat(e.amount || e.total || 0); });
    const catArr = Object.entries(categories).sort((a, b) => b[1] - a[1]);
    const catColors = [C.danger, C.orange, C.warning, C.purple, C.pink, C.teal, C.accent, C.slate];

    return (<View>
      <View style={st.kpiRow}>
        <KPICard title="Total Expenses" value={fmtGBP(kpis.totalExpenses)} icon="cash-minus" color={C.danger} subtitle={`${expenses.length} transactions`} />
        <KPICard title="Payroll" value={fmtGBP(kpis.payrollCost)} icon="account-cash" color={C.purple} subtitle={`${kpis.employeeCount} employees`} />
      </View>
      <View style={st.card}>
        <Text style={st.cardTitle}>Expenses by Month</Text>
        <MiniBar data={monthlyData.map(m => ({ value: m.expenses + m.payroll, label: m.month }))} color={C.danger} />
      </View>
      <View style={st.card}>
        <Text style={st.cardTitle}>By Category</Text>
        {catArr.map(([cat, amount], i) => (
          <View key={cat}>
            <ProgressBar value={amount} max={catArr[0][1]} color={catColors[i % catColors.length]} label={cat} showAmount />
          </View>))}
        {catArr.length === 0 && <Text style={{ color: '#94A3B8', textAlign: 'center', padding: 20 }}>No expense data</Text>}
      </View>
      <View style={st.card}>
        <Text style={st.cardTitle}>Recent Expenses</Text>
        {[...expenses].sort((a, b) => new Date(b.created_at || b.date || 0) - new Date(a.created_at || a.date || 0)).slice(0, 10).map((e, i) => (
          <View key={e.id || i} style={st.listRow}>
            <View style={{ flex: 1 }}>
              <Text style={st.listName}>{e.description || e.title || e.category || 'Expense'}</Text>
              <Text style={st.listMeta}>{e.category || e.expense_type || ''} · {fmtDate(e.created_at || e.expense_date || e.date)}</Text></View>
            <Text style={[st.listAmount, { color: C.danger }]}>-{fmtGBP(e.amount || e.total)}</Text>
          </View>))}
      </View>
    </View>);
  };

  // ══════════════════════════════════════════════════════════════════
  // TAB: PROFIT & LOSS
  // ══════════════════════════════════════════════════════════════════
  const renderPL = () => (<View>
    <View style={st.kpiRow}>
      <KPICard title="Net Profit" value={fmtGBP(kpis.netProfit)} icon="chart-line" color={kpis.netProfit >= 0 ? C.success : C.danger} size="full" subtitle={`Margin: ${fmtPct(kpis.profitMargin)}`} />
    </View>
    {/* P&L Statement */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Profit & Loss Statement</Text>
      <View style={{ marginTop: 12 }}>
        <View style={st.plRow}><Text style={st.plLabel}>Revenue</Text><Text style={[st.plValue, { color: C.success }]}>{fmtGBP(kpis.totalRevenue)}</Text></View>
        <View style={st.plDivider} />
        <View style={st.plRow}><Text style={st.plLabel}>Operating Expenses</Text><Text style={[st.plValue, { color: C.danger }]}>-{fmtGBP(kpis.totalExpenses - kpis.payrollCost)}</Text></View>
        <View style={st.plRow}><Text style={st.plLabel}>Payroll</Text><Text style={[st.plValue, { color: C.danger }]}>-{fmtGBP(kpis.payrollCost)}</Text></View>
        <View style={[st.plDivider, { borderColor: '#0F172A' }]} />
        <View style={st.plRow}><Text style={[st.plLabel, { fontWeight: '800' }]}>Gross Profit</Text><Text style={[st.plValue, { fontWeight: '800', color: kpis.netProfit >= 0 ? C.success : C.danger }]}>{fmtGBP(kpis.netProfit)}</Text></View>
        <View style={st.plDivider} />
        <View style={st.plRow}><Text style={st.plLabel}>Corporation Tax (19%)</Text><Text style={[st.plValue, { color: C.warning }]}>-{fmtGBP(kpis.taxLiability)}</Text></View>
        <View style={[st.plDivider, { borderColor: '#0F172A' }]} />
        <View style={st.plRow}><Text style={[st.plLabel, { fontWeight: '900', fontSize: 16 }]}>Net Profit After Tax</Text><Text style={[st.plValue, { fontWeight: '900', fontSize: 16, color: (kpis.netProfit - kpis.taxLiability) >= 0 ? C.success : C.danger }]}>{fmtGBP(kpis.netProfit - kpis.taxLiability)}</Text></View>
      </View>
    </View>
    {/* Monthly P&L */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Monthly P&L</Text>
      <MiniBar data={monthlyData.map(m => ({ value: Math.max(0, m.profit), label: m.month }))} color={C.profit} label="Profit by Month" />
      {monthlyData.filter(m => m.revenue > 0 || m.expenses > 0).map((m, i) => (
        <View key={i} style={st.listRow}>
          <Text style={{ fontSize: 13, fontWeight: '600', color: '#334155', width: 40 }}>{m.month}</Text>
          <Text style={{ fontSize: 12, color: C.success, flex: 1, textAlign: 'center' }}>{fmtK(m.revenue)}</Text>
          <Text style={{ fontSize: 12, color: C.danger, flex: 1, textAlign: 'center' }}>-{fmtK(m.expenses + m.payroll)}</Text>
          <Text style={{ fontSize: 12, fontWeight: '700', color: m.profit >= 0 ? C.success : C.danger, flex: 1, textAlign: 'right' }}>{fmtK(m.profit)}</Text>
        </View>))}
    </View>
  </View>);

  // ══════════════════════════════════════════════════════════════════
  // TAB: INVOICES
  // ══════════════════════════════════════════════════════════════════
  const renderInvoices = () => (<View>
    <View style={st.kpiRow}>
      <KPICard title="Total" value={String(kpis.invoiceCount)} icon="file-document-multiple" color={C.accent} />
      <KPICard title="Paid" value={String(kpis.paidCount)} icon="check-circle" color={C.success} />
      <KPICard title="Pending" value={String(kpis.pendingCount)} icon="clock" color={C.warning} />
      <KPICard title="Overdue" value={String(kpis.overdueCount)} icon="alert-circle" color={C.danger} />
    </View>
    <View style={st.card}>
      <Text style={st.cardTitle}>Outstanding: {fmtGBP(kpis.outstanding)}</Text>
      <ProgressBar value={kpis.outstanding} max={kpis.totalRevenue || 1} color={C.warning} label="Outstanding vs Total" showAmount />
    </View>
    {/* Aged Receivables */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Aged Receivables</Text>
      <View style={{ flexDirection: 'row', gap: 4, marginTop: 10 }}>
        {[{ l: 'Current', v: agingBuckets.current, c: C.success },
          { l: '1-30d', v: agingBuckets.d30, c: C.warning },
          { l: '31-60d', v: agingBuckets.d60, c: C.orange },
          { l: '61-90d', v: agingBuckets.d90, c: C.danger },
          { l: '90d+', v: agingBuckets.d90plus, c: '#7F1D1D' },
        ].map((b, i) => (
          <View key={i} style={{ flex: 1, backgroundColor: b.c + '10', borderRadius: 8, padding: 8, alignItems: 'center' }}>
            <Text style={{ fontSize: 12, fontWeight: '900', color: b.c }}>{fmtK(b.v)}</Text>
            <Text style={{ fontSize: 8, color: '#64748B', marginTop: 2 }}>{b.l}</Text>
          </View>
        ))}
      </View>
      <View style={{ height: 10, flexDirection: 'row', borderRadius: 5, overflow: 'hidden', marginTop: 8 }}>
        {[{ v: agingBuckets.current, c: C.success }, { v: agingBuckets.d30, c: C.warning },
          { v: agingBuckets.d60, c: C.orange }, { v: agingBuckets.d90, c: C.danger }, { v: agingBuckets.d90plus, c: '#7F1D1D' },
        ].map((b, i) => {
          const total = agingBuckets.current + agingBuckets.d30 + agingBuckets.d60 + agingBuckets.d90 + agingBuckets.d90plus;
          const pct = total > 0 ? (b.v / total * 100) : 0;
          return pct > 0 ? <View key={i} style={{ width: pct + '%', height: '100%', backgroundColor: b.c }} /> : null;
        })}
      </View>
    </View>

    <View style={st.card}>
      <Text style={st.cardTitle}>All Invoices ({invoices.length})</Text>
      {invoices.length === 0 ? <Text style={{ color: '#94A3B8', textAlign: 'center', padding: 20 }}>No invoices found</Text>
      : [...invoices].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 20).map((inv, i) => {
        const s = (inv.status || 'pending').toLowerCase();
        const sc = s === 'paid' ? C.success : s === 'overdue' ? C.danger : C.warning;
        return (<View key={inv.id || i} style={st.listRow}>
          <View style={[st.listDot, { backgroundColor: sc }]} />
          <View style={{ flex: 1, marginLeft: 8 }}>
            <Text style={st.listName}>{inv.client_name || inv.customer_name || inv.invoice_number || '#' + (inv.id || i)}</Text>
            <Text style={st.listMeta}>{fmtDate(inv.created_at || inv.invoice_date)} · {inv.invoice_number || ''}</Text></View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={[st.listAmount, { color: sc }]}>{fmtGBP(inv.total || inv.amount)}</Text>
            <Text style={{ fontSize: 10, color: sc, fontWeight: '600', textTransform: 'capitalize' }}>{s}</Text></View>
        </View>);
      })}
    </View>
  </View>);

  // ══════════════════════════════════════════════════════════════════
  // TAB: PAYROLL
  // ══════════════════════════════════════════════════════════════════
  const renderPayroll = () => (<View>
    <View style={st.kpiRow}>
      <KPICard title="Total Payroll" value={fmtGBP(kpis.payrollCost)} icon="account-cash" color={C.purple} />
      <KPICard title="Employees" value={String(kpis.employeeCount)} icon="account-group" color={C.accent} />
    </View>
    <View style={st.card}>
      <Text style={st.cardTitle}>Payroll by Month</Text>
      <MiniBar data={monthlyData.map(m => ({ value: m.payroll, label: m.month }))} color={C.purple} />
    </View>
    <View style={st.card}>
      <Text style={st.cardTitle}>Payroll Breakdown</Text>
      {[{ l: 'Gross Pay', v: kpis.payrollCost, c: C.purple },
        { l: 'Employer NI (13.8%)', v: kpis.payrollCost * 0.138, c: C.orange },
        { l: 'Pension (3%)', v: kpis.payrollCost * 0.03, c: C.teal },
        { l: 'Total Employment Cost', v: kpis.payrollCost * 1.168, c: C.danger },
      ].map((m, i) => (<View key={i} style={st.metricRow}>
        <Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{fmtGBP(m.v)}</Text></View>))}
    </View>
    <View style={st.card}>
      <Text style={st.cardTitle}>Recent Payroll Records</Text>
      {payroll.length === 0 ? <Text style={{ color: '#94A3B8', textAlign: 'center', padding: 20 }}>No payroll data</Text>
      : [...payroll].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)).slice(0, 10).map((p, i) => (
        <View key={p.id || i} style={st.listRow}>
          <View style={{ flex: 1 }}>
            <Text style={st.listName}>{p.employee_name || p.name || 'Employee'}</Text>
            <Text style={st.listMeta}>{fmtDate(p.pay_date || p.created_at)} · {p.pay_period || ''}</Text></View>
          <Text style={[st.listAmount, { color: C.purple }]}>{fmtGBP(p.gross_pay || p.total || p.amount)}</Text>
        </View>))}
    </View>
  </View>);

  // ══════════════════════════════════════════════════════════════════
  // TAB: BUDGETS
  // ══════════════════════════════════════════════════════════════════
  const renderBudgets = () => (<View>
    <View style={st.kpiRow}>
      <KPICard title="Total Budget" value={fmtGBP(kpis.budgetTotal)} icon="chart-donut" color={C.accent} />
      <KPICard title="Remaining" value={fmtGBP(kpis.budgetRemaining)} icon="wallet" color={kpis.budgetRemaining >= 0 ? C.success : C.danger} />
    </View>
    <View style={st.card}>
      <Text style={st.cardTitle}>Budget Allocation</Text>
      {budgets.length === 0 ? <Text style={{ color: '#94A3B8', textAlign: 'center', padding: 20 }}>No budgets set</Text>
      : budgets.map((b, i) => {
        const total = parseFloat(b.total || b.amount || b.budget_amount || 0);
        const used = parseFloat(b.spent || b.used || b.actual || 0);
        const pct = total > 0 ? (used / total) * 100 : 0;
        return (<View key={b.id || i} style={{ marginBottom: 14 }}>
          <ProgressBar value={used} max={total} color={pct > 90 ? C.danger : pct > 70 ? C.warning : C.success}
            label={b.name || b.category || b.department || 'Budget'} showAmount />
        </View>);
      })}
    </View>
  </View>);

  // ══════════════════════════════════════════════════════════════════
  // TAB: TAX
  // ══════════════════════════════════════════════════════════════════
  const renderTax = () => (<View>
    <View style={st.kpiRow}>
      <KPICard title="Corp Tax (19%)" value={fmtGBP(kpis.taxLiability)} icon="percent" color={C.warning} subtitle="On net profit" />
      <KPICard title="VAT Due (20%)" value={fmtGBP(kpis.vatDue)} icon="receipt" color={C.orange} subtitle="Output - Input" />
    </View>
    <View style={st.card}>
      <Text style={st.cardTitle}>UK Tax Summary</Text>
      <View style={{ marginTop: 12 }}>
        {[{ l: 'Taxable Profit', v: fmtGBP(Math.max(0, kpis.netProfit)), c: C.accent },
          { l: 'Corporation Tax Rate', v: '19%', c: C.warning },
          { l: 'Corporation Tax Due', v: fmtGBP(kpis.taxLiability), c: C.warning },
          { l: '', v: '', c: 'divider' },
          { l: 'Output VAT (Sales)', v: fmtGBP(kpis.totalRevenue * 0.2), c: C.orange },
          { l: 'Input VAT (Purchases)', v: '-' + fmtGBP((kpis.totalExpenses - kpis.payrollCost) * 0.2), c: C.success },
          { l: 'VAT Payable', v: fmtGBP(kpis.vatDue), c: C.orange },
          { l: '', v: '', c: 'divider' },
          { l: 'Employer NI Estimate', v: fmtGBP(kpis.payrollCost * 0.138), c: C.purple },
          { l: 'Total Tax Liabilities', v: fmtGBP(kpis.taxLiability + kpis.vatDue + kpis.payrollCost * 0.138), c: C.danger },
        ].map((m, i) => m.c === 'divider' ? <View key={i} style={st.plDivider} /> : (
          <View key={i} style={st.metricRow}><Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{m.v}</Text></View>))}
      </View>
    </View>
    <View style={{ padding: 12, backgroundColor: '#FEF3C7', borderRadius: 10, marginHorizontal: 16, marginTop: 8 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center' }}><MaterialCommunityIcons name="alert-circle" size={16} color={C.warning} />
        <Text style={{ fontSize: 12, color: '#92400E', fontWeight: '600', marginLeft: 6 }}>Estimates Only</Text></View>
      <Text style={{ fontSize: 11, color: '#92400E', marginTop: 4, lineHeight: 16 }}>Tax calculations are simplified estimates. Actual liability depends on allowances, reliefs, and HMRC rules. Consult a qualified accountant for accurate tax planning.</Text>
    </View>
  </View>);

  // ══════════════════════════════════════════════════════════════════
  // HEADER + TABS + MAIN RENDER
  // ══════════════════════════════════════════════════════════════════
  const renderHeader = () => (
    <LinearGradient colors={['#0F172A', '#1E293B']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={() => navigation?.goBack()} style={st.hBtn}><Ionicons name="arrow-back" size={22} color="#FFF" /></TouchableOpacity>
        {companyLogo && typeof companyLogo === 'string' && companyLogo.startsWith('http') ? <Image source={{ uri: companyLogo || undefined }} style={{ width: 28, height: 28, borderRadius: 8, marginLeft: 6 }} onError={() => setCompanyLogo(null)} /> : null}
        <View style={{ flex: 1, marginLeft: 8 }}>
          <Text style={st.hTitle}>Financial Analytics</Text>
          <Text style={{ color: '#94A3B8', fontSize: 11 }}>{companyName}</Text></View>
        <TouchableOpacity style={st.hBtn} onPress={exportReport}><MaterialCommunityIcons name="export-variant" size={20} color="#FFF" /></TouchableOpacity>
        <TouchableOpacity style={[st.hBtn, { marginLeft: 4 }]} onPress={onRefresh}><MaterialCommunityIcons name="refresh" size={20} color="#FFF" /></TouchableOpacity>
      </View>
      {/* Period Selector */}
      <View style={{ flexDirection: 'row', gap: 6, marginTop: 10, paddingLeft: 4 }}>
        {['month', 'quarter', 'ytd', 'year'].map(p => (
          <TouchableOpacity key={p} style={[st.periodBtn, selectedPeriod === p && st.periodActive]} onPress={() => setSelectedPeriod(p)}>
            <Text style={[st.periodTxt, selectedPeriod === p && { color: '#FFF' }]}>{p.toUpperCase()}</Text></TouchableOpacity>))}
      </View>
    </LinearGradient>);

  const renderTabs = () => (
    <View style={st.tabsRow}><ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 8 }}>
      {TABS.map(t => (
        <TouchableOpacity key={t.k} style={[st.tab, activeTab === t.k && st.activeTab]} onPress={() => setActiveTab(t.k)}>
          <MaterialCommunityIcons name={t.i} size={14} color={activeTab === t.k ? C.accent : '#94A3B8'} />
          <Text style={[st.tabTxt, activeTab === t.k && { color: C.accent, fontWeight: '700' }]}>{t.l}</Text></TouchableOpacity>))}
    </ScrollView></View>);

  const renderContent = () => { switch (activeTab) {
    case 'overview': return renderOverview();
    case 'revenue': return renderRevenue();
    case 'expenses': return renderExpenses();
    case 'pl': return renderPL();
    case 'invoices': return renderInvoices();
    case 'payroll': return renderPayroll();
    case 'budgets': return renderBudgets();
    case 'cashflow': return renderCashFlow();
    case 'tax': return renderTax();
    case 'forecast': return renderForecast();
    default: return renderOverview();
  }};

  // ══════════════════════════════════════════════════════════════════
  // TAB: CASH FLOW
  // ══════════════════════════════════════════════════════════════════
  const renderCashFlow = () => (<View>
    <View style={st.kpiRow}>
      <KPICard title="Inflows" value={fmtK(cashFlowData.inflows)} icon="arrow-down-bold-circle" color={C.success} subtitle="Total received" />
      <KPICard title="Outflows" value={fmtK(cashFlowData.outflows)} icon="arrow-up-bold-circle" color={C.danger} subtitle="Total paid out" />
      <KPICard title="Net Cash" value={fmtK(cashFlowData.net)} icon="bank-transfer" color={cashFlowData.net >= 0 ? C.profit : C.danger} />
      <KPICard title="Runway" value={cashFlowData.runway > 12 ? '12+ mo' : cashFlowData.runway.toFixed(1) + ' mo'} icon="timer-sand" color={cashFlowData.runway > 6 ? C.success : cashFlowData.runway > 3 ? C.warning : C.danger} />
    </View>

    {/* Cash Flow Statement */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Cash Flow Statement</Text>
      <View style={{ marginTop: 12 }}>
        <Text style={{ fontSize: 13, fontWeight: '800', color: C.success, marginBottom: 6 }}>OPERATING ACTIVITIES</Text>
        {[{ l: 'Revenue Received', v: kpis.totalRevenue - kpis.outstanding, c: C.success },
          { l: 'Outstanding Receivables', v: kpis.outstanding, c: C.warning },
        ].map((m, i) => (<View key={i} style={st.metricRow}><Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{fmtGBP(m.v)}</Text></View>))}
        <View style={st.plDivider} />
        <Text style={{ fontSize: 13, fontWeight: '800', color: C.danger, marginBottom: 6, marginTop: 8 }}>CASH OUTFLOWS</Text>
        {[{ l: 'Operating Expenses', v: kpis.totalExpenses - kpis.payrollCost, c: C.danger },
          { l: 'Payroll & Benefits', v: kpis.payrollCost * 1.168, c: C.purple },
          { l: 'Tax Provisions', v: kpis.taxLiability + kpis.vatDue, c: C.orange },
        ].map((m, i) => (<View key={i} style={st.metricRow}><Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>-{fmtGBP(m.v)}</Text></View>))}
        <View style={[st.plDivider, { borderColor: '#0F172A' }]} />
        <View style={st.metricRow}>
          <Text style={[st.metricLabel, { fontWeight: '900', fontSize: 15 }]}>Net Cash Position</Text>
          <Text style={[st.metricValue, { fontWeight: '900', fontSize: 15, color: cashFlowData.net >= 0 ? C.success : C.danger }]}>{fmtGBP(cashFlowData.net)}</Text>
        </View>
      </View>
    </View>

    {/* Monthly Cash Flow Chart */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Monthly Cash Flow</Text>
      <View style={{ flexDirection: 'row', height: 100, alignItems: 'flex-end', marginTop: 12, gap: 4, paddingHorizontal: 4 }}>
        {monthlyData.map((m, i) => {
          const maxV = Math.max(...monthlyData.map(d => Math.max(d.revenue, d.expenses + d.payroll)), 1);
          const netM = m.revenue - m.expenses - m.payroll;
          return (<View key={i} style={{ flex: 1, alignItems: 'center' }}>
            <View style={{ width: '70%', height: Math.max(2, (Math.abs(netM) / maxV) * 80), backgroundColor: netM >= 0 ? C.success : C.danger, borderRadius: 2, marginBottom: netM < 0 ? 0 : 2 }} />
            <Text style={{ fontSize: 7, color: '#94A3B8', marginTop: 2 }}>{m.month}</Text>
          </View>);
        })}
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 16, marginTop: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}><View style={{ width: 10, height: 10, backgroundColor: C.success, borderRadius: 2 }} /><Text style={{ fontSize: 10, color: '#64748B' }}>Positive</Text></View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}><View style={{ width: 10, height: 10, backgroundColor: C.danger, borderRadius: 2 }} /><Text style={{ fontSize: 10, color: '#64748B' }}>Negative</Text></View>
      </View>
    </View>

    {/* Burn Rate */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Burn Rate & Runway</Text>
      {[{ l: 'Monthly Revenue (Avg)', v: fmtGBP(cashFlowData.inflows / 12), c: C.success },
        { l: 'Monthly Burn Rate', v: fmtGBP(cashFlowData.monthlyBurn), c: C.danger },
        { l: 'Monthly Net', v: fmtGBP((cashFlowData.inflows / 12) - cashFlowData.monthlyBurn), c: (cashFlowData.inflows / 12) > cashFlowData.monthlyBurn ? C.success : C.danger },
        { l: 'Cash Runway', v: cashFlowData.runway > 12 ? '12+ months' : cashFlowData.runway.toFixed(1) + ' months', c: cashFlowData.runway > 6 ? C.success : cashFlowData.runway > 3 ? C.warning : C.danger },
        { l: 'Break-Even Point', v: cashFlowData.monthlyBurn > 0 ? fmtGBP(cashFlowData.monthlyBurn) + '/mo min' : 'N/A', c: C.accent },
      ].map((m, i) => (<View key={i} style={st.metricRow}><Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{m.v}</Text></View>))}
    </View>

    {/* Cash Flow Ratios */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Cash Flow Ratios</Text>
      {[{ l: 'Operating Cash Flow Ratio', v: fmtPct(cashFlowData.inflows > 0 ? (cashFlowData.net / cashFlowData.inflows * 100) : 0), d: 'Net cash / Total inflows' },
        { l: 'Cash Flow Margin', v: fmtPct(kpis.totalRevenue > 0 ? (cashFlowData.net / kpis.totalRevenue * 100) : 0), d: 'Net cash / Revenue' },
        { l: 'Cash Conversion Ratio', v: fmtPct(kpis.totalRevenue > 0 ? ((kpis.totalRevenue - kpis.outstanding) / kpis.totalRevenue * 100) : 0), d: 'Cash received / Revenue billed' },
      ].map((r, i) => (
        <View key={i} style={{ paddingVertical: 10, borderBottomWidth: i < 2 ? 1 : 0, borderBottomColor: '#F1F5F9' }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Text style={{ fontSize: 13, color: '#334155', fontWeight: '600' }}>{r.l}</Text>
            <Text style={{ fontSize: 14, fontWeight: '800', color: C.accent }}>{r.v}</Text>
          </View>
          <Text style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{r.d}</Text>
        </View>
      ))}
    </View>
  </View>);

  // ══════════════════════════════════════════════════════════════════
  // TAB: FORECASTING
  // ══════════════════════════════════════════════════════════════════
  const renderForecast = () => (<View>
    <View style={st.kpiRow}>
      <KPICard title="Rev Forecast (3mo)" value={fmtK(forecastData.revForecast[2]?.value || 0)} icon="chart-timeline-variant" color={C.success} />
      <KPICard title="Exp Forecast (3mo)" value={fmtK(forecastData.expForecast[2]?.value || 0)} icon="chart-timeline-variant-shimmer" color={C.danger} />
    </View>

    {/* Revenue Forecast */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Revenue Forecast</Text>
      <Text style={{ fontSize: 11, color: '#94A3B8', marginBottom: 10 }}>Based on current run rate and trend analysis</Text>
      {forecastData.revForecast.map((f, i) => (
        <View key={i} style={st.metricRow}>
          <Text style={st.metricLabel}>{f.label}</Text>
          <Text style={[st.metricValue, { color: C.success }]}>{fmtGBP(f.value)}</Text>
        </View>
      ))}
    </View>

    {/* Expense Forecast */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Expense Forecast</Text>
      <Text style={{ fontSize: 11, color: '#94A3B8', marginBottom: 10 }}>Projected at current spending rate</Text>
      {forecastData.expForecast.map((f, i) => (
        <View key={i} style={st.metricRow}>
          <Text style={st.metricLabel}>{f.label}</Text>
          <Text style={[st.metricValue, { color: C.danger }]}>{fmtGBP(f.value)}</Text>
        </View>
      ))}
    </View>

    {/* Profit Forecast */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Projected Profit</Text>
      <Text style={{ fontSize: 11, color: '#94A3B8', marginBottom: 10 }}>Estimated net profit at current trajectory</Text>
      {forecastData.profitForecast.map((f, i) => {
        const isProfit = f.value >= 0;
        return (
          <View key={i} style={st.metricRow}>
            <Text style={st.metricLabel}>{f.label}</Text>
            <Text style={[st.metricValue, { color: isProfit ? C.success : C.danger }]}>{isProfit ? '' : '-'}{fmtGBP(Math.abs(f.value))}</Text>
          </View>
        );
      })}
    </View>

    {/* Forecast Chart */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Revenue vs Expense Projection</Text>
      <View style={{ flexDirection: 'row', height: 100, alignItems: 'flex-end', marginTop: 12, gap: 6 }}>
        {forecastData.revForecast.map((r, i) => {
          const maxV = Math.max(...forecastData.revForecast.map(d => d.value), ...forecastData.expForecast.map(d => d.value), 1);
          const e = forecastData.expForecast[i];
          return (<View key={i} style={{ flex: 1, alignItems: 'center' }}>
            <View style={{ flexDirection: 'row', alignItems: 'flex-end', height: 80, gap: 2 }}>
              <View style={{ width: 8, height: Math.max(2, (r.value / maxV) * 76), backgroundColor: C.success, borderRadius: 2 }} />
              <View style={{ width: 8, height: Math.max(2, ((e?.value || 0) / maxV) * 76), backgroundColor: C.danger + '80', borderRadius: 2 }} />
            </View>
            <Text style={{ fontSize: 7, color: '#94A3B8', marginTop: 2, textAlign: 'center' }}>{r.label.replace('Next ', '')}</Text>
          </View>);
        })}
      </View>
      <View style={{ flexDirection: 'row', justifyContent: 'center', gap: 16, marginTop: 8 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}><View style={{ width: 10, height: 10, backgroundColor: C.success, borderRadius: 2 }} /><Text style={{ fontSize: 10, color: '#64748B' }}>Revenue</Text></View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}><View style={{ width: 10, height: 10, backgroundColor: C.danger + '80', borderRadius: 2 }} /><Text style={{ fontSize: 10, color: '#64748B' }}>Expenses</Text></View>
      </View>
    </View>

    {/* Key Assumptions */}
    <View style={st.card}>
      <Text style={st.cardTitle}>Forecast Assumptions</Text>
      {['Revenue based on average monthly run rate with trend adjustment',
        'Expenses projected at constant current rate',
        'Tax calculated at 19% corporation tax + 20% VAT',
        'Payroll includes 13.8% employer NI + 3% pension',
        'No seasonal adjustments applied',
        'Forecasts are estimates and may vary significantly',
      ].map((a, i) => (
        <View key={i} style={{ flexDirection: 'row', gap: 6, marginBottom: 6 }}>
          <MaterialCommunityIcons name="information-outline" size={14} color="#94A3B8" style={{ marginTop: 1 }} />
          <Text style={{ fontSize: 11, color: '#475569', flex: 1, lineHeight: 16 }}>{a}</Text>
        </View>
      ))}
    </View>

    {/* ROI Analysis */}
    <View style={st.card}>
      <Text style={st.cardTitle}>ROI Analysis</Text>
      {[{ l: 'Total Investment', v: fmtGBP(kpis.totalExpenses), c: C.danger },
        { l: 'Total Return', v: fmtGBP(kpis.totalRevenue), c: C.success },
        { l: 'ROI', v: fmtPct(kpis.totalExpenses > 0 ? ((kpis.totalRevenue - kpis.totalExpenses) / kpis.totalExpenses * 100) : 0), c: kpis.totalRevenue > kpis.totalExpenses ? C.success : C.danger },
        { l: 'Payback Period', v: kpis.totalRevenue > 0 ? ((kpis.totalExpenses / (kpis.totalRevenue / 12))).toFixed(1) + ' months' : 'N/A', c: C.accent },
      ].map((m, i) => (<View key={i} style={st.metricRow}><Text style={st.metricLabel}>{m.l}</Text><Text style={[st.metricValue, { color: m.c }]}>{m.v}</Text></View>))}
    </View>
  </View>);

  if (isLoading) return <SafeAreaView style={st.loading}><StatusBar barStyle="light-content" /><ActivityIndicator size="large" color={C.accent} /><Text style={{ color: '#64748B', marginTop: 12 }}>Loading financial data...</Text></SafeAreaView>;

  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" backgroundColor={C.primary} />
      {renderHeader()}
      {renderTabs()}
      <ScrollView style={{ flex: 1, backgroundColor: '#F8FAFC' }} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} />} showsVerticalScrollIndicator={false}>
        {renderContent()}
        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>);
}

// ══════════════════════════════════════════════════════════════════════════
// STYLES
// ══════════════════════════════════════════════════════════════════════════
const st = StyleSheet.create({
  container: { flex: 1, backgroundColor: '#F8FAFC' },
  loading: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: '#F8FAFC' },
  header: { paddingTop: 8, paddingBottom: 12, paddingHorizontal: 16 },
  headerRow: { flexDirection: 'row', alignItems: 'center' },
  hBtn: { width: 36, height: 36, borderRadius: 12, backgroundColor: 'rgba(255,255,255,0.1)', justifyContent: 'center', alignItems: 'center' },
  hTitle: { fontSize: 20, fontWeight: '900', color: '#FFF', letterSpacing: -0.3 },
  periodBtn: { paddingHorizontal: 12, paddingVertical: 5, borderRadius: 14, backgroundColor: 'rgba(255,255,255,0.08)' },
  periodActive: { backgroundColor: C.accent },
  periodTxt: { fontSize: 11, fontWeight: '700', color: '#94A3B8' },
  tabsRow: { backgroundColor: '#FFF', borderBottomWidth: 1, borderBottomColor: '#E2E8F0' },
  tab: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 10, paddingVertical: 10, marginRight: 2, borderRadius: 14 },
  activeTab: { backgroundColor: '#EFF6FF' },
  tabTxt: { fontSize: 11, color: '#94A3B8', marginLeft: 4, fontWeight: '500' },
  // KPI
  kpiRow: { flexDirection: 'row', flexWrap: 'wrap', padding: 16, gap: 8 },
  kpiCard: { width: (width - 40) / 2, backgroundColor: '#FFF', borderRadius: 14, padding: 14, borderWidth: 1, borderColor: '#E2E8F0' },
  kpiIcon: { width: 28, height: 28, borderRadius: 8, justifyContent: 'center', alignItems: 'center' },
  kpiTitle: { fontSize: 11, color: '#64748B', fontWeight: '600', marginLeft: 6, flex: 1 },
  kpiValue: { fontSize: 22, fontWeight: '900', marginTop: 4 },
  kpiSub: { fontSize: 11, color: '#94A3B8', marginTop: 2 },
  trendBadge: { flexDirection: 'row', alignItems: 'center', borderRadius: 8, paddingHorizontal: 5, paddingVertical: 2 },
  // Card
  card: { backgroundColor: '#FFF', marginHorizontal: 16, marginBottom: 12, padding: 16, borderRadius: 14, borderWidth: 1, borderColor: '#E2E8F0' },
  cardTitle: { fontSize: 15, fontWeight: '800', color: '#0F172A' },
  // Metrics
  metricRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: '#F8FAFC' },
  metricLabel: { fontSize: 13, color: '#475569' },
  metricValue: { fontSize: 13, fontWeight: '700' },
  // P&L
  plRow: { flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 10 },
  plLabel: { fontSize: 14, color: '#334155' },
  plValue: { fontSize: 14, fontWeight: '700' },
  plDivider: { borderBottomWidth: 1, borderColor: '#E2E8F0', marginVertical: 2 },
  // List
  listRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: '#F8FAFC' },
  listDot: { width: 8, height: 8, borderRadius: 4 },
  listName: { fontSize: 13, fontWeight: '600', color: '#1E293B' },
  listMeta: { fontSize: 11, color: '#94A3B8', marginTop: 1 },
  listAmount: { fontSize: 14, fontWeight: '800' },
  // Alert
  alertCard: { flexDirection: 'row', alignItems: 'center', backgroundColor: '#FEE2E2', marginHorizontal: 16, marginBottom: 8, padding: 12, borderRadius: 10, borderWidth: 1, borderColor: '#FECACA' },
});
